import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight, ArrowLeft, Sparkles, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/api';

const TIERS = [
  {
    id: 'start',
    name: 'Старт',
    price: 0,
    priceLabel: 'Бесплатно',
    description: 'Для знакомства с платформой',
    badge: null,
    features: [
      { text: 'До 20 пациентов', included: true },
      { text: 'До 5 часов записей в неделю', included: true },
      { text: 'Базовая CRM', included: true },
      { text: 'Расписание и запись', included: true },
      { text: '0 администраторов', included: true },
      { text: 'Финансовая аналитика', included: false },
      { text: 'Файловое хранилище', included: false },
      { text: 'Защищённый мессенджер', included: false },
      { text: 'Свой домен', included: false },
    ],
  },
  {
    id: 'practic',
    name: 'Практик',
    price: 690,
    priceLabel: '690 ₽/мес',
    description: 'Для частного специалиста',
    badge: null,
    features: [
      { text: 'Безлимит пациентов', included: true },
      { text: 'До 20 часов записей в неделю', included: true },
      { text: 'Базовая CRM', included: true },
      { text: 'Расписание и запись', included: true },
      { text: '1 администратор', included: true },
      { text: 'Финансовая аналитика', included: true },
      { text: 'Файловое хранилище', included: true },
      { text: 'Защищённый мессенджер', included: false },
      { text: 'Свой домен', included: false },
    ],
  },
  {
    id: 'profi',
    name: 'Профи',
    price: 1690,
    priceLabel: '1 690 ₽/мес',
    description: 'Для кабинета с командой',
    badge: 'Популярный',
    features: [
      { text: 'Безлимит пациентов', included: true },
      { text: 'Безлимит часов записей', included: true },
      { text: 'Базовая CRM', included: true },
      { text: 'Расписание и запись', included: true },
      { text: 'До 3 администраторов', included: true },
      { text: 'Финансовая аналитика', included: true },
      { text: 'Файловое хранилище', included: true },
      { text: 'Защищённый мессенджер', included: true },
      { text: 'Свой домен', included: true },
    ],
  },
  {
    id: 'clinic',
    name: 'Организация',
    price: -1,
    priceLabel: 'По запросу',
    description: 'Для клиник, салонов и учебных центров',
    badge: 'B2B',
    features: [
      { text: 'Безлимит пациентов', included: true },
      { text: 'Безлимит часов записей', included: true },
      { text: 'Полная CRM', included: true },
      { text: 'Мульти-специалистное расписание', included: true },
      { text: 'Безлимит администраторов', included: true },
      { text: 'Финансы + разделение доходов', included: true },
      { text: 'Файловое хранилище', included: true },
      { text: 'Защищённый мессенджер', included: true },
      { text: 'Свой домен + управление кабинетами', included: true },
    ],
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      if (data.authenticated) {
        setIsAuthenticated(true);
        api.get('/subscription').then(({ data: sub }) => {
          setCurrentPlan(sub.plan?.id || 'start');
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (planId === 'clinic') return;
    try {
      await api.put(`/subscription/upgrade?plan_id=${planId}`);
      setCurrentPlan(planId);
      toast.success(`Тариф обновлён до «${TIERS.find(t => t.id === planId)?.name}»`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--app-bg, #0c0a09)' }}>
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        {/* Back / exit */}
        <button data-testid="pricing-back" onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
          className="inline-flex items-center gap-1.5 text-sm mb-10 rounded-lg px-2 py-1 -ml-2 transition-colors hover:bg-white/5"
          style={{ color: 'var(--app-text-muted)' }}>
          <ArrowLeft size={16} /> {isAuthenticated ? 'В кабинет' : 'На главную'}
        </button>

        {/* Header */}
        <div className="text-center mb-14">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" style={{ color: 'var(--app-text, #e7e5e4)' }}>
            Тарифы
          </h1>
          <p className="text-base sm:text-lg max-w-xl mx-auto" style={{ color: 'var(--app-text-muted, #78716c)' }}>
            Выберите подходящий план для вашей практики
          </p>
        </div>

        {/* Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {TIERS.map((tier) => {
            const isCurrent = currentPlan === tier.id;
            const isPopular = tier.badge === 'Популярный';
            const isClinic = tier.id === 'clinic';
            return (
              <div key={tier.id} data-testid={`pricing-tier-${tier.id}`}
                className={`relative rounded-2xl border p-6 flex flex-col transition-all ${
                  isPopular
                    ? 'border-amber-600/60 bg-amber-950/10 shadow-lg shadow-amber-900/10'
                    : isClinic
                    ? 'border-violet-700/40 bg-violet-950/10'
                    : 'border-stone-800/60 bg-stone-900/30'
                }`}>
                {tier.badge && (
                  <Badge className={`absolute -top-2.5 left-4 text-[10px] font-semibold ${
                    isPopular
                      ? 'bg-amber-600 text-white border-amber-500'
                      : 'bg-violet-600 text-white border-violet-500'
                  }`}>
                    {tier.badge}
                  </Badge>
                )}

                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-1">
                    {isClinic && <Building2 size={18} className="text-violet-400" />}
                    <h3 className="text-lg font-bold" style={{ color: 'var(--app-text, #e7e5e4)' }}>
                      {tier.name}
                    </h3>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--app-text-muted, #78716c)' }}>
                    {tier.description}
                  </p>
                </div>

                <div className="mb-6">
                  <span className={`text-2xl font-bold ${
                    isPopular ? 'text-amber-400' : isClinic ? 'text-violet-400' : ''
                  }`} style={!isPopular && !isClinic ? { color: 'var(--app-text, #e7e5e4)' } : {}}>
                    {tier.priceLabel}
                  </span>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {tier.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      {f.included ? (
                        <Check size={15} className={`mt-0.5 shrink-0 ${
                          isPopular ? 'text-amber-500' : isClinic ? 'text-violet-400' : 'text-emerald-500'
                        }`} />
                      ) : (
                        <X size={15} className="mt-0.5 shrink-0 text-stone-700" />
                      )}
                      <span style={{ color: f.included ? 'var(--app-text, #d6d3d1)' : 'var(--app-text-muted, #57534e)' }}>
                        {f.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {isClinic ? (
                  <Button data-testid="pricing-clinic-contact" variant="outline"
                    className="w-full border-violet-700/50 text-violet-400 hover:bg-violet-900/20"
                    onClick={() => window.location.href = 'mailto:support@propraxis.ru'}>
                    <Building2 size={15} className="mr-1.5" />
                    Связаться с отделом продаж
                  </Button>
                ) : isCurrent ? (
                  <Button disabled className="w-full bg-stone-800 text-stone-500 cursor-default">
                    Текущий тариф
                  </Button>
                ) : (
                  <Button data-testid={`pricing-select-${tier.id}`}
                    onClick={() => handleUpgrade(tier.id)}
                    className={`w-full ${
                      isPopular
                        ? 'bg-amber-600 hover:bg-amber-500 text-white'
                        : 'bg-stone-800 hover:bg-stone-700 text-stone-200'
                    }`}>
                    {tier.price === 0 ? 'Начать бесплатно' : 'Выбрать'}
                    <ArrowRight size={15} className="ml-1.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="mt-16 text-center">
          <p className="text-sm" style={{ color: 'var(--app-text-muted, #78716c)' }}>
            Нужна помощь с выбором?{' '}
            <a href="mailto:support@propraxis.ru" className="text-amber-500 hover:text-amber-400 underline underline-offset-2">
              Напишите нам
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
